import {
  Children,
  createContext,
  useContext,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { cx } from "../../lib/cx";
import { useT } from "../../lib/i18n";
import { Button } from "./Button";
import { dsControl, dsFont } from "./styles";

export type TableColumn = {
  id: string;
  header: string;
  /**
   * CSS grid track. The stage table uses the reference tracks
   * `minmax(0,1.5fr) minmax(260px,2fr) 120px auto` so every row shares one grid.
   */
  width?: string;
  /** `end` pins the action column to the right edge of its track. */
  align?: "start" | "end";
};

const ColumnsContext = createContext<TableColumn[]>([]);

type TableProps = {
  columns: TableColumn[];
  children: ReactNode;
  /** Body rows to show. Extra children are not rendered. Omit to show every row. */
  maxRows?: number;
};

/**
 * Header plus pill rows on one grid. Separate per-row grids size `auto`
 * from that row's button, which shifts the stage column. Subgrid keeps
 * every stepper, caption, due date and action on the same tracks.
 */
export function Table({ columns, children, maxRows }: TableProps) {
  const template = columns.map((column) => column.width ?? "minmax(0,1fr)").join(" ");
  const items = Children.toArray(children);
  const cap = maxRows == null ? items.length : Math.max(0, Math.floor(maxRows));
  const visible = items.length > cap ? items.slice(0, cap) : items;
  return (
    <div data-ds="" data-testid="ds-table" className={cx(dsFont, "max-[760px]:overflow-x-auto")}>
      <div
        className="grid max-[760px]:min-w-[600px]"
        style={{ gridTemplateColumns: template, columnGap: "14px" }}
      >
        <div
          data-testid="ds-table-head"
          className="grid items-center px-3 pb-1.5 text-ds-label font-normal text-ds-ink-3"
          style={{ gridColumn: "1 / -1", gridTemplateColumns: "subgrid" }}
        >
          {columns.map((column) => (
            <span
              key={column.id}
              className={cx("min-w-0", column.align === "end" && "justify-self-end text-right")}
            >
              {column.header}
            </span>
          ))}
        </div>
        <ColumnsContext.Provider value={columns}>{visible}</ColumnsContext.Provider>
      </div>
    </div>
  );
}

type RowProps = {
  cells: ReactNode[];
  selected?: boolean;
  onSelect?: () => void;
  /**
   * Opens this RFQ. Home passes `onNavigate("rfq", id)`. A plain left click
   * on `href` is handed here so the shell can route without a full load.
   */
  onOpen?: (event: MouseEvent<HTMLElement>) => void;
  /** RFQ destination. Same place `onOpen` goes, as a real link. */
  href?: string;
};

/** Secondary pill. Same look as `Button variant="secondary"`, for the link form. */
const openClass = cx(
  dsControl,
  "inline-flex h-[38px] shrink-0 items-center justify-center rounded-ds-pill bg-ds-glass-strong px-[15px] py-0 text-ds-body font-semibold whitespace-nowrap text-ds-ink no-underline hover:bg-ds-amber hover:text-ds-on-amber",
);

/**
 * Soft row card. Every row uses the same hover fill — no selected highlight.
 * A transparent block-axis border is the gap between cards and stays inside
 * the 58px row, so the shared columns do not move and the page does not grow.
 * An empty cell stays empty. Clicks on an inner button do not select the row.
 */
export function TableRow({ cells, selected = false, onSelect, onOpen, href }: RowProps) {
  const columns = useContext(ColumnsContext);
  const t = useT();
  const openLabel = t("ds.gallery.open");
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Enter" && event.key !== " ") return;
    if ((event.target as HTMLElement).closest("button, a")) return;
    event.preventDefault();
    onSelect?.();
  }
  return (
    <div
      role="button"
      tabIndex={0}
      aria-selected={selected}
      data-testid="ds-table-row"
      data-selected={selected ? "true" : "false"}
      className={cx(
        "grid min-h-ds-row-table items-center rounded-ds-row border-x-0 border-y-4 border-solid border-transparent bg-ds-hover bg-clip-padding px-3 text-left text-ds-item font-normal text-ds-ink",
      )}
      style={{ gridColumn: "1 / -1", gridTemplateColumns: "subgrid" }}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("button, a")) return;
        onSelect?.();
      }}
      onKeyDown={onKeyDown}
    >
      {cells.map((cell, index) => (
        <div
          key={index}
          className={cx(
            "min-w-0",
            columns[index]?.align === "end" && "justify-self-end text-right",
          )}
        >
          {cell ?? null}
        </div>
      ))}
      {onOpen || href ? (
        <div
          className={cx(
            "min-w-0",
            columns[cells.length]?.align === "end" && "justify-self-end text-right",
          )}
        >
          {href ? (
            <a
              href={href}
              data-testid="ds-table-open"
              data-variant="secondary"
              className={openClass}
              onClick={(event) => {
                if (!onOpen) return;
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                onOpen(event);
              }}
            >
              {openLabel}
            </a>
          ) : (
            <Button variant="secondary" data-testid="ds-table-open" onClick={onOpen}>
              {openLabel}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
